import is from '@sindresorhus/is';
import { useCallback, useRef } from 'react';

import type { JobProfileSkill } from '~/shared/data-contract/skill';
import type { TrackingEvent } from '~/shared/tracking/tracking';

import { useMixpanelTracking } from '../../../tracking/mixpanel/use-mixpanel-tracking';
import { useJsaTrackingContext } from './use-jsa-tracking-context';

type ItemType = 'job-level' | 'job-role';

type SkillChangeTrackingParams = {
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly parentRoleId?: string;
    readonly versionId: string;
};

type UseTrackSkillChangesOptions = {
    readonly architectureId: string;
};

type UseTrackSkillChangesResult = {
    readonly captureInitialSkills: (skills: readonly JobProfileSkill[]) => void;
    readonly trackSkillChanges: (finalSkills: readonly JobProfileSkill[], params: SkillChangeTrackingParams) => void;
};

type BasePayloadContext = {
    readonly architectureId: string;
    readonly editorRoles: readonly string[];
    readonly itemType: ItemType;
    readonly levelId: string | undefined;
    readonly roleId: string | undefined;
    readonly tenantId: string;
    readonly timestamp: string;
    readonly userId: string;
    readonly versionId: string;
    readonly viewMode: 'board' | 'table';
};

const getLevelId = (itemType: ItemType, itemId: string): string | undefined =>
    itemType === 'job-level' ? itemId : undefined;

const getRoleId = (itemType: ItemType, itemId: string, parentRoleId?: string): string | undefined =>
    itemType === 'job-role' ? itemId : parentRoleId;

const detectAddedSkills = (
    finalSkills: readonly JobProfileSkill[],
    initialSkillsMap: ReadonlyMap<string, JobProfileSkill>,
    basePayload: BasePayloadContext,
): readonly TrackingEvent[] =>
    finalSkills
        .filter((skill) => !initialSkillsMap.has(skill.id))
        .map((skill) => ({
            name: 'role-skill-change' as const,
            payload: {
                ...basePayload,
                changeType: 'add' as const,
                entityType: 'skill' as const,
                method: 'manual' as const,
                skillId: skill.id,
            },
        }));

const detectRemovedSkills = (
    initialSkills: readonly JobProfileSkill[],
    finalSkillsMap: ReadonlyMap<string, JobProfileSkill>,
    basePayload: BasePayloadContext,
): readonly TrackingEvent[] =>
    initialSkills
        .filter((skill) => !finalSkillsMap.has(skill.id))
        .map((skill) => ({
            name: 'role-skill-change' as const,
            payload: {
                ...basePayload,
                changeType: 'remove' as const,
                entityType: 'skill' as const,
                method: 'manual' as const,
                skillId: skill.id,
            },
        }));

const detectProficiencyChanges = (
    finalSkills: readonly JobProfileSkill[],
    initialSkillsMap: ReadonlyMap<string, JobProfileSkill>,
    basePayload: BasePayloadContext,
): readonly TrackingEvent[] =>
    finalSkills
        .filter((finalSkill) => {
            const initialSkill = initialSkillsMap.get(finalSkill.id);

            return !is.nullOrUndefined(initialSkill) && initialSkill.level !== finalSkill.level;
        })
        .map((finalSkill) => {
            const initialSkill = initialSkillsMap.get(finalSkill.id);

            return {
                name: 'role-skill-change' as const,
                payload: {
                    ...basePayload,
                    changeType: 'proficiency_change' as const,
                    entityType: 'skill' as const,
                    method: 'manual' as const,
                    newProficiencyLevel: finalSkill.level ?? undefined,
                    oldProficiencyLevel: initialSkill?.level ?? undefined,
                    skillId: finalSkill.id,
                },
            };
        });

const detectCompetencyChanges = (
    finalSkills: readonly JobProfileSkill[],
    initialSkillsMap: ReadonlyMap<string, JobProfileSkill>,
    basePayload: BasePayloadContext,
): readonly TrackingEvent[] => {
    const events: TrackingEvent[] = [];

    finalSkills.forEach((finalSkill) => {
        const initialSkill = initialSkillsMap.get(finalSkill.id);
        if (is.nullOrUndefined(initialSkill)) {
            return;
        }

        const hadCompetency = is.nonEmptyString(initialSkill.competencyId);
        const hasCompetency = is.nonEmptyString(finalSkill.competencyId);

        if (!hadCompetency && hasCompetency) {
            events.push({
                name: 'role-skill-change',
                payload: {
                    ...basePayload,
                    changeType: 'link_to_competency',
                    competencyId: finalSkill.competencyId,
                    entityType: 'skill',
                    method: 'manual',
                    skillId: finalSkill.id,
                },
            });
        } else if (hadCompetency && !hasCompetency) {
            events.push({
                name: 'role-skill-change',
                payload: {
                    ...basePayload,
                    changeType: 'unlink_from_competency',
                    competencyId: initialSkill.competencyId,
                    entityType: 'skill',
                    method: 'manual',
                    skillId: finalSkill.id,
                },
            });
        }
    });

    return events;
};

export const useTrackSkillChanges = (options: UseTrackSkillChangesOptions): UseTrackSkillChangesResult => {
    const { architectureId } = options;
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();
    const initialSkillsRef = useRef<readonly JobProfileSkill[]>([]);

    const captureInitialSkills = useCallback((skills: readonly JobProfileSkill[]) => {
        initialSkillsRef.current = skills;
    }, []);

    const trackSkillChanges = useCallback(
        (finalSkills: readonly JobProfileSkill[], params: SkillChangeTrackingParams) => {
            const initialSkills = initialSkillsRef.current;
            const initialSkillsMap = new Map(initialSkills.map((skill) => [skill.id, skill]));
            const finalSkillsMap = new Map(finalSkills.map((skill) => [skill.id, skill]));

            const basePayload: BasePayloadContext = {
                ...trackingContext,
                architectureId,
                itemType: params.itemType,
                levelId: getLevelId(params.itemType, params.itemId),
                roleId: getRoleId(params.itemType, params.itemId, params.parentRoleId),
                versionId: params.versionId,
            };

            const events = [
                ...detectAddedSkills(finalSkills, initialSkillsMap, basePayload),
                ...detectRemovedSkills(initialSkills, finalSkillsMap, basePayload),
                ...detectProficiencyChanges(finalSkills, initialSkillsMap, basePayload),
                ...detectCompetencyChanges(finalSkills, initialSkillsMap, basePayload),
            ];

            events.forEach(trackEvent);

            initialSkillsRef.current = [];
        },
        [trackEvent, trackingContext, architectureId],
    );

    return { captureInitialSkills, trackSkillChanges };
};
